/**
 * Botão "Baixar tudo (ZIP)".
 *
 * - Sticky abaixo da file-list
 * - Habilita quando ≥1 entry done
 * - Click: loading state → fflate.zip() async → Blob → <a download> → revoke
 * - Filenames já vêm sanitizados do file-list (displayName)
 * - "No-worse": entries com compressedSize >= size ficam fora (encoded === original)
 *
 * A11y: button.disabled quando não tem done; aria-busy durante loading; loading label via i18n.
 */
import { zip } from 'fflate';
import { t } from '@/i18n/index.js';
import { showToast } from '@/ui/toast.js';

// fflate é callback-style — promisify manual.
// NOTA: zip(files, undefined, cb) crasha no bundle Vite com
// "Cannot read properties of undefined (reading 'comment')" —
// precisa de opts = {} explícito.
const zipAsync = (files, opts = {}) =>
  new Promise((resolve, reject) => {
    zip(files, opts, (err, data) => (err ? reject(err) : resolve(data)));
  });

/**
 * @param {HTMLButtonElement} btn
 * @param {{
 *   getDone: () => Array<{displayName: string, type: string, _compressedBuffer: ArrayBuffer, size: number, compressedSize: number}>,
 * }} deps
 */
export function mountDownload(btn, { getDone }) {
  if (!btn) return;
  if (btn.dataset.mounted === 'download') return;
  btn.dataset.mounted = 'download';

  let busy = false;

  function refresh() {
    const done = getDone();
    btn.disabled = done.length === 0 || busy;
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.querySelector('[data-i18n="download.button"]').textContent = t('download.loading');
    } else {
      btn.removeAttribute('aria-busy');
      btn.querySelector('[data-i18n="download.button"]').textContent = t('download.button');
    }
  }

  btn.addEventListener('click', async () => {
    if (busy) return;
    const done = getDone();
    if (done.length === 0) {
      showToast({ message: t('download.empty'), type: 'info' });
      return;
    }

    busy = true;
    refresh();

    try {
      // Filtra entries com "no-worse" (compressedSize >= size) — encoded === original
      const files = {};
      let added = 0;
      for (const e of done) {
        if (e.compressedSize >= e.size) continue;
        files[e.displayName] = new Uint8Array(e._compressedBuffer);
        added++;
      }

      if (added === 0) {
        showToast({ message: t('download.empty'), type: 'info' });
        return;
      }

      const zipBytes = await zipAsync(files);
      const blob = new Blob([zipBytes], { type: 'application/zip' });

      // timestamp pra nome do arquivo
      const ts = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, 19);
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `compress-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // revoke no próximo tick pra garantir que o download iniciou
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast({
        message: t('download.success', { count: added }),
        type: 'success',
      });
    } catch (err) {
      console.error('[download] erro:', err);
      showToast({ message: `${t('toast.encodeError')}: ${err.message}`, type: 'error' });
    } finally {
      busy = false;
      refresh();
    }
  });

  // Expor refresh pra main.js chamar quando algum status muda
  return { refresh };
}
